import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Button from "../../components/ui/Button";
import { Checkbox } from "../../components/ui/Checkbox";
import Icon from "../../components/AppIcon";
import PhotoUploadCard from "../installation-workflow/components/PhotoUploadCard";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  customers as customersAPI,
  commissioningWork as commissioningWorkAPI,
  staffAssignments as staffAssignmentsAPI,
  notifications as notificationsAPI,
} from "../../lib/database";

const buildCapturedLocation = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { latitude: lat, longitude: lng };
};

const emptyVerification = () => ({
  cable_routing: false,
  lightning_arrester_checked: false,
  civil_structure: false,
  earthing_pits: false,
  inverter_functionality_confirmed: false,
  safety_systems_operation: false,
});

const SAVE_DEBOUNCE_MS = 1500;
const COMMISSIONING_STEPS = [
  { number: 1, title: "Testing", icon: "FlaskConical" },
  { number: 2, title: "Verification", icon: "ShieldCheck" },
];
const STATUS_ORDER = {
  survey_not_started: 1,
  survey_inprogress: 2,
  survey_complete: 3,
  survey_unworkable: 3,
  transport_pending: 4,
  transport_inprogress: 5,
  transport_complete: 6,
  civil_pending: 7,
  civil_inprogress: 8,
  civil_complete: 9,
  technical_pending: 10,
  technical_inprogress: 11,
  technical_complete: 12,
  commissioning_not_started: 13,
  commissioning_inprogress: 14,
  commissioning_complete: 15,
};

const CommissioningWorkflow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [customerId, setCustomerId] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  const [voltageMeterPhoto, setVoltageMeterPhoto] = useState("");
  const [mainMeterPhoto, setMainMeterPhoto] = useState("");
  const [panelNumber1, setPanelNumber1] = useState("");
  const [panelNumber2, setPanelNumber2] = useState("");
  const [panelNumber3, setPanelNumber3] = useState("");
  const [panelNumber4, setPanelNumber4] = useState("");
  const [inverterNumber, setInverterNumber] = useState("");
  const [customerPanelSidePhoto, setCustomerPanelSidePhoto] = useState("");
  const [customerPanelFrontPhoto, setCustomerPanelFrontPhoto] = useState("");
  const [customerInverterPhoto, setCustomerInverterPhoto] = useState("");
  const [verification, setVerification] = useState(emptyVerification);

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionLocked, setSubmissionLocked] = useState(false);
  const saveTimeoutRef = useRef(null);
  const formRef = useRef({
    voltageMeterPhoto: "",
    mainMeterPhoto: "",
    panelNumber1: "",
    panelNumber2: "",
    panelNumber3: "",
    panelNumber4: "",
    inverterNumber: "",
    customerPanelSidePhoto: "",
    customerPanelFrontPhoto: "",
    customerInverterPhoto: "",
    verification: emptyVerification(),
  });

  useEffect(() => {
    formRef.current = {
      voltageMeterPhoto,
      mainMeterPhoto,
      panelNumber1,
      panelNumber2,
      panelNumber3,
      panelNumber4,
      inverterNumber,
      customerPanelSidePhoto,
      customerPanelFrontPhoto,
      customerInverterPhoto,
      verification: { ...verification },
    };
  }, [
    voltageMeterPhoto,
    mainMeterPhoto,
    panelNumber1,
    panelNumber2,
    panelNumber3,
    panelNumber4,
    inverterNumber,
    customerPanelSidePhoto,
    customerPanelFrontPhoto,
    customerInverterPhoto,
    verification,
  ]);

  useEffect(() => {
    const id = searchParams.get("customerId");
    setCustomerId(id || null);
  }, [searchParams]);

  useEffect(() => {
    const load = async () => {
      if (!customerId || user?.role !== "worker") {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [customer, row] = await Promise.all([
          customersAPI.getById(customerId),
          commissioningWorkAPI.getByCustomerId(customerId).catch(() => null),
        ]);
        setCustomerData(customer || null);
        if (row) {
          setVoltageMeterPhoto(row.voltage_meter_photo || "");
          setMainMeterPhoto(row.main_meter_serial_photo || "");
          setPanelNumber1(row.panel_number_1 || "");
          setPanelNumber2(row.panel_number_2 || "");
          setPanelNumber3(row.panel_number_3 || "");
          setPanelNumber4(row.panel_number_4 || "");
          setInverterNumber(row.inverter_number || "");
          setCustomerPanelSidePhoto(row.customer_photo_with_panels_side || "");
          setCustomerPanelFrontPhoto(row.customer_photo_with_panels_front || "");
          setCustomerInverterPhoto(row.customer_photo_with_inverter || "");
          setVerification({
            ...emptyVerification(),
            ...(row.verification && typeof row.verification === "object"
              ? row.verification
              : {}),
          });
          // Allow edits after submit (pending); lock only after final approval.
          const locked = row.approval_status === "approved";
          setSubmissionLocked(!!locked);
        } else {
          setSubmissionLocked(false);
        }
      } catch (e) {
        console.error(e);
        showToast("Failed to load customer.", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customerId, user?.role, showToast]);

  const handleSave = useCallback(
    async ({ silent = false } = {}) => {
      if (!customerId || submissionLocked) return;
      const f = formRef.current;
      setIsSaving(true);
      try {
        await commissioningWorkAPI.upsertForCustomer(customerId, {
          voltage_meter_photo: f.voltageMeterPhoto || null,
          main_meter_serial_photo: f.mainMeterPhoto || null,
          panel_number_1: f.panelNumber1 || null,
          panel_number_2: f.panelNumber2 || null,
          panel_number_3: f.panelNumber3 || null,
          panel_number_4: f.panelNumber4 || null,
          inverter_number: f.inverterNumber || null,
          customer_photo_with_panels_side: f.customerPanelSidePhoto || null,
          customer_photo_with_panels_front: f.customerPanelFrontPhoto || null,
          customer_photo_with_inverter: f.customerInverterPhoto || null,
          verification: f.verification || emptyVerification(),
        });
        const customer = await customersAPI.getById(customerId);
        if (
          (STATUS_ORDER[customer?.status] || 0) <
          (STATUS_ORDER.commissioning_inprogress || 0)
        ) {
          await customersAPI.update(customerId, {
            status: "commissioning_inprogress",
          });
          setCustomerData((prev) =>
            prev ? { ...prev, status: "commissioning_inprogress" } : prev,
          );
        }
        if (!silent) {
          showToast("Progress saved.", "success");
        }
        setLastSavedAt(new Date());
      } catch (e) {
        console.error(e);
        if (!silent) {
          showToast("Failed to save progress.", "error");
        }
      } finally {
        setIsSaving(false);
      }
    },
    [customerId, showToast, submissionLocked],
  );

  const scheduleSave = useCallback(() => {
    if (!customerId || user?.role !== "worker" || submissionLocked)
      return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      handleSave({ silent: true }).catch(() => {});
    }, SAVE_DEBOUNCE_MS);
  }, [customerId, user?.role, submissionLocked, handleSave]);

  const flushPendingSave = useCallback(async () => {
    if (!customerId || submissionLocked) return;
    if (!saveTimeoutRef.current) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = null;
    await handleSave({ silent: true });
  }, [customerId, submissionLocked, handleSave]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!saveTimeoutRef.current) return;
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      handleSave({ silent: true }).catch(() => {});
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [handleSave]);

  const onPhotoChange = async (file) => {
    if (!file || !customerId) return undefined;
    try {
      const url = await commissioningWorkAPI.uploadPhoto(
        customerId,
        file,
        "main_meter_serial",
      );
      setMainMeterPhoto(url);
      scheduleSave();
      return url;
    } catch (err) {
      console.error(err);
      showToast("Failed to upload photo.", "error");
      throw err;
    }
  };

  const handleRemovePhoto = () => {
    if (submissionLocked) return;
    setMainMeterPhoto("");
    scheduleSave();
  };

  const onCommissioningPhotoChange = async (file, photoId, setter) => {
    if (!file || !customerId) return undefined;
    try {
      const url = await commissioningWorkAPI.uploadPhoto(customerId, file, photoId);
      setter(url);
      scheduleSave();
      return url;
    } catch (err) {
      console.error(err);
      showToast("Failed to upload photo.", "error");
      throw err;
    }
  };

  const onVoltageMeterPhotoChange = async (file) => {
    if (!file || !customerId) return undefined;
    try {
      const url = await commissioningWorkAPI.uploadPhoto(
        customerId,
        file,
        "voltage_meter",
      );
      setVoltageMeterPhoto(url);
      scheduleSave();
      return url;
    } catch (err) {
      console.error(err);
      showToast("Failed to upload photo.", "error");
      throw err;
    }
  };

  const handleRemoveVoltageMeterPhoto = () => {
    if (submissionLocked) return;
    setVoltageMeterPhoto("");
    scheduleSave();
  };

  const handleSubmit = async () => {
    if (submissionLocked) return;
    const f = formRef.current;
    if (
      !f.mainMeterPhoto ||
      !f.customerPanelSidePhoto ||
      !f.customerPanelFrontPhoto ||
      !f.customerInverterPhoto
    ) {
      showToast(
        "Upload all required commissioning photos.",
        "error",
      );
      setStep(1);
      return;
    }
    if (
      !f.panelNumber1 ||
      !f.panelNumber2 ||
      !f.panelNumber3 ||
      !f.panelNumber4 ||
      !f.inverterNumber
    ) {
      showToast(
        "Enter panel numbers 1 to 4 and inverter number.",
        "error",
      );
      setStep(1);
      return;
    }
    const v = f.verification || {};
    const keys = Object.keys(emptyVerification());
    if (!keys.every((k) => v[k] === true)) {
      showToast("Confirm all verification checkboxes.", "error");
      setStep(2);
      return;
    }

    setIsSubmitting(true);
    try {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      await commissioningWorkAPI.upsertForCustomer(customerId, {
        voltage_meter_photo: f.voltageMeterPhoto,
        main_meter_serial_photo: f.mainMeterPhoto,
        panel_number_1: f.panelNumber1,
        panel_number_2: f.panelNumber2,
        panel_number_3: f.panelNumber3,
        panel_number_4: f.panelNumber4,
        inverter_number: f.inverterNumber,
        customer_photo_with_panels_side: f.customerPanelSidePhoto,
        customer_photo_with_panels_front: f.customerPanelFrontPhoto,
        customer_photo_with_inverter: f.customerInverterPhoto,
        verification: { ...f.verification },
        commissioning_completed_at: new Date().toISOString(),
        submitted_by: user?.id || null,
        approval_status: "pending",
        approval_decision_by: null,
        approval_decision_at: null,
        disapproval_reason: null,
      });
      const latestCustomer = await customersAPI.getById(customerId);
      if (
        (STATUS_ORDER.commissioning_complete || 0) >
        (STATUS_ORDER[latestCustomer?.status] || 0)
      ) {
        await customersAPI.update(customerId, {
          status: "commissioning_complete",
        });
      }

      try {
        const existing = await staffAssignmentsAPI.getByCustomerAndUser(
          customerId,
          user.id,
        );
        if (existing) {
          await staffAssignmentsAPI.markCompleted(customerId, user.id);
        } else {
          await staffAssignmentsAPI.create(customerId, user.id);
          await staffAssignmentsAPI.markCompleted(customerId, user.id);
        }
      } catch (assignErr) {
        console.error(assignErr);
      }

      try {
        await notificationsAPI.notifyClusterManagerOnWorkCompletion(
          customerId,
          "commissioning",
        );
      } catch (nErr) {
        console.error(nErr);
      }

      showToast("Commissioning submitted for approval.", "success");
      navigate("/worker-dashboard");
    } catch (err) {
      console.error(err);
      showToast("Failed to submit.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = async () => {
    await flushPendingSave();
    navigate("/worker-dashboard");
  };

  const handleNext = async () => {
    await flushPendingSave();
    if (step < COMMISSIONING_STEPS.length) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = async () => {
    await flushPendingSave();
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleStepClick = async (stepNumber) => {
    if (isSaving || isSubmitting || stepNumber === step) return;
    await flushPendingSave();
    setStep(stepNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (user?.role !== "worker") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <p className="text-muted-foreground">Unauthorized.</p>
      </div>
    );
  }

  if (!customerId) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="content-area p-6">
          <p className="text-muted-foreground">Missing customer.</p>
          <Button className="mt-4" onClick={handleBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const verificationItems = [
    { key: "cable_routing", label: "Cable routing" },
    { key: "lightning_arrester_checked", label: "Lightning arrester checked" },
    { key: "civil_structure", label: "Civil structure" },
    { key: "earthing_pits", label: "Earthing pits" },
    {
      key: "inverter_functionality_confirmed",
      label: "Inverter functionality confirmed",
    },
    { key: "safety_systems_operation", label: "Safety systems operation" },
  ];
  const overlayMeta = {
    customerName: customerData?.name || "",
    village: customerData?.village || "",
    region:
      customerData?.regions?.name || customerData?.regions?.region_name || "",
  };
  const capturedLocation = buildCapturedLocation(
    customerData?.latitude,
    customerData?.longitude,
  );

  return (
    <>
      <Helmet>
        <title>Commissioning Workflow</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="content-area">
          <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon
                    name="FlaskConical"
                    size={24}
                    className="text-primary"
                  />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
                    Commissioning Workflow
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground mt-1">
                    Record testing and submit for approval
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {customerData?.name || "Customer"}{" "}
                    {customerData?.cin ? `· ${customerData.cin}` : ""}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleBack}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  className="ml-auto"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>

            {submissionLocked && (
              <div className="mb-6 p-3 rounded-lg border border-border bg-muted/40 text-sm text-foreground">
                This commissioning record was submitted and can no longer be
                edited here.
              </div>
            )}

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6 md:mb-8">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-lg md:text-xl font-semibold text-foreground min-w-0">
                  Commissioning - Step {step} of {COMMISSIONING_STEPS.length}
                </h2>
                <span className="text-sm text-muted-foreground flex-shrink-0">
                  {Math.round((step / COMMISSIONING_STEPS.length) * 100)}%
                  Complete
                </span>
              </div>
              <div className="overflow-x-auto -mx-1 px-1 pb-1 md:overflow-visible md:mx-0 md:px-0 md:pb-0">
                <div className="flex items-center flex-nowrap min-w-0 md:flex-wrap md:justify-between gap-2">
                  {COMMISSIONING_STEPS.map((s, index) => {
                    const stepComplete =
                      (s.number === 1 &&
                        mainMeterPhoto &&
                        panelNumber1 &&
                        panelNumber2 &&
                        panelNumber3 &&
                        panelNumber4 &&
                        inverterNumber &&
                        customerPanelSidePhoto &&
                        customerPanelFrontPhoto &&
                        customerInverterPhoto) ||
                      (s.number === 2 &&
                        Object.keys(emptyVerification()).every(
                          (k) => verification?.[k] === true,
                        ));
                    const isActive = step === s.number;
                    return (
                      <React.Fragment key={s.number}>
                        <button
                          type="button"
                          onClick={() => handleStepClick(s.number)}
                          disabled={isSaving || isSubmitting}
                          className={`flex flex-col items-center flex-shrink-0 min-w-[72px] md:min-w-0 md:flex-1 rounded-lg px-1 py-1 -mx-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ${
                            isActive
                              ? "bg-primary/5"
                              : "hover:bg-muted/80 cursor-pointer"
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                              isActive
                                ? "bg-primary border-primary text-primary-foreground"
                                : stepComplete
                                  ? "bg-success border-success text-white"
                                  : "bg-muted border-border text-muted-foreground"
                            }`}
                          >
                            {stepComplete ? (
                              <Icon name="Check" size={20} />
                            ) : (
                              <span className="text-sm font-semibold">
                                {s.number}
                              </span>
                            )}
                          </div>
                          <span
                            className={`text-xs mt-2 text-center whitespace-nowrap max-w-[100px] md:max-w-none leading-tight ${
                              isActive
                                ? "text-primary font-medium"
                                : "text-muted-foreground"
                            }`}
                          >
                            {s.title}
                          </span>
                        </button>
                        {index < COMMISSIONING_STEPS.length - 1 && (
                          <div
                            className={`flex-shrink-0 w-3 md:flex-1 md:w-auto h-0.5 mx-1 md:mx-2 transition-all ${
                              stepComplete ? "bg-success" : "bg-border"
                            }`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="space-y-6 md:space-y-8">
              {step === 1 && (
                <>
                  <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                    <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                      Testing
                    </h3>
                    <div className="space-y-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-foreground mb-2">
                          Panel Numbers
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              Panel number 1
                            </label>
                            <input
                              type="text"
                              value={panelNumber1}
                              onChange={(e) => {
                                setPanelNumber1(e.target.value);
                                scheduleSave();
                              }}
                              onBlur={flushPendingSave}
                              disabled={submissionLocked}
                              placeholder="Enter panel number 1"
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-muted/40"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              Panel number 2
                            </label>
                            <input
                              type="text"
                              value={panelNumber2}
                              onChange={(e) => {
                                setPanelNumber2(e.target.value);
                                scheduleSave();
                              }}
                              onBlur={flushPendingSave}
                              disabled={submissionLocked}
                              placeholder="Enter panel number 2"
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-muted/40"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              Panel number 3
                            </label>
                            <input
                              type="text"
                              value={panelNumber3}
                              onChange={(e) => {
                                setPanelNumber3(e.target.value);
                                scheduleSave();
                              }}
                              onBlur={flushPendingSave}
                              disabled={submissionLocked}
                              placeholder="Enter panel number 3"
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-muted/40"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-muted-foreground">
                              Panel number 4
                            </label>
                            <input
                              type="text"
                              value={panelNumber4}
                              onChange={(e) => {
                                setPanelNumber4(e.target.value);
                                scheduleSave();
                              }}
                              onBlur={flushPendingSave}
                              disabled={submissionLocked}
                              placeholder="Enter panel number 4"
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-muted/40"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="max-w-md">
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Inverter Number
                        </label>
                        <input
                          type="text"
                          value={inverterNumber}
                          onChange={(e) => {
                            setInverterNumber(e.target.value);
                            scheduleSave();
                          }}
                          onBlur={flushPendingSave}
                          disabled={submissionLocked}
                          placeholder="Enter inverter number"
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-muted/40"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <PhotoUploadCard
                        title="Voltage meter photo"
                        description="Upload or capture a clear voltage meter reading photo"
                        photoUrl={voltageMeterPhoto}
                        readOnly={submissionLocked}
                        overlayMeta={overlayMeta}
                        capturedLocation={capturedLocation}
                        onUpload={(url) => {
                          setVoltageMeterPhoto(url);
                          scheduleSave();
                        }}
                        onRemove={handleRemoveVoltageMeterPhoto}
                        uploadFunction={
                          !submissionLocked && customerId
                            ? (file) => onVoltageMeterPhotoChange(file)
                            : null
                        }
                      />
                      <PhotoUploadCard
                        title="Main meter serial number photo"
                        description="Upload or capture a clear photo of the main meter serial number"
                        photoUrl={mainMeterPhoto}
                        readOnly={submissionLocked}
                        overlayMeta={overlayMeta}
                        capturedLocation={capturedLocation}
                        onUpload={(url) => {
                          setMainMeterPhoto(url);
                          scheduleSave();
                        }}
                        onRemove={handleRemovePhoto}
                        uploadFunction={
                          !submissionLocked && customerId
                            ? (file) => onPhotoChange(file)
                            : null
                        }
                      />
                      <PhotoUploadCard
                        title="Customer photo with panels (side)"
                        description="Upload or capture customer photo with panels side view"
                        photoUrl={customerPanelSidePhoto}
                        readOnly={submissionLocked}
                        overlayMeta={overlayMeta}
                        capturedLocation={capturedLocation}
                        onUpload={(url) => {
                          setCustomerPanelSidePhoto(url);
                          scheduleSave();
                        }}
                        onRemove={() => {
                          if (submissionLocked) return;
                          setCustomerPanelSidePhoto("");
                          scheduleSave();
                        }}
                        uploadFunction={
                          !submissionLocked && customerId
                            ? (file) =>
                                onCommissioningPhotoChange(
                                  file,
                                  "customer_panels_side",
                                  setCustomerPanelSidePhoto,
                                )
                            : null
                        }
                      />
                      <PhotoUploadCard
                        title="Customer photo with panels (front)"
                        description="Upload or capture customer photo with panels front view"
                        photoUrl={customerPanelFrontPhoto}
                        readOnly={submissionLocked}
                        overlayMeta={overlayMeta}
                        capturedLocation={capturedLocation}
                        onUpload={(url) => {
                          setCustomerPanelFrontPhoto(url);
                          scheduleSave();
                        }}
                        onRemove={() => {
                          if (submissionLocked) return;
                          setCustomerPanelFrontPhoto("");
                          scheduleSave();
                        }}
                        uploadFunction={
                          !submissionLocked && customerId
                            ? (file) =>
                                onCommissioningPhotoChange(
                                  file,
                                  "customer_panels_front",
                                  setCustomerPanelFrontPhoto,
                                )
                            : null
                        }
                      />
                      <PhotoUploadCard
                        title="Customer photo with inverter"
                        description="Upload or capture customer photo with inverter"
                        photoUrl={customerInverterPhoto}
                        readOnly={submissionLocked}
                        overlayMeta={overlayMeta}
                        capturedLocation={capturedLocation}
                        onUpload={(url) => {
                          setCustomerInverterPhoto(url);
                          scheduleSave();
                        }}
                        onRemove={() => {
                          if (submissionLocked) return;
                          setCustomerInverterPhoto("");
                          scheduleSave();
                        }}
                        uploadFunction={
                          !submissionLocked && customerId
                            ? (file) =>
                                onCommissioningPhotoChange(
                                  file,
                                  "customer_inverter",
                                  setCustomerInverterPhoto,
                                )
                            : null
                        }
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                    <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                      Verification
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Confirm each item below.
                    </p>
                    <div className="space-y-3">
                      {verificationItems.map(({ key, label }) => (
                        <Checkbox
                          key={key}
                          checked={!!verification[key]}
                          disabled={submissionLocked}
                          onChange={(e) => {
                            setVerification((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }));
                            scheduleSave();
                          }}
                          label={label}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ChevronLeft"
                  iconPosition="left"
                  onClick={handlePrevious}
                  disabled={step === 1 || isSaving || isSubmitting}
                  className="w-full sm:w-auto"
                >
                  Previous
                </Button>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
                  {step < COMMISSIONING_STEPS.length ? (
                    <Button
                      variant="default"
                      size="lg"
                      iconName="ChevronRight"
                      iconPosition="right"
                      onClick={handleNext}
                      disabled={isSaving || isSubmitting}
                    >
                      Next
                    </Button>
                  ) : (
                    !submissionLocked && (
                      <Button
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting || isSaving}
                        iconName="Send"
                        size="lg"
                      >
                        Submit
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>

            {isSaving && !submissionLocked && (
              <p className="text-sm text-primary/80 animate-pulse mt-2">
                Saving...
              </p>
            )}
            {!isSaving && !submissionLocked && lastSavedAt && (
              <p className="text-xs text-success mt-2">Data saved</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CommissioningWorkflow;
